import { useMutation, useQuery } from '@tanstack/react-query';
import { getApi } from '../api';
import { transformSnake2Camel } from './utils';
import moment from 'moment';
import { SIMPLE_DATE_FORMAT } from '../utils/utils';

const isValidDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false;
  const start = moment(startDate, SIMPLE_DATE_FORMAT, true);
  const end = moment(endDate, SIMPLE_DATE_FORMAT, true);
  return start.isValid() && end.isValid() && start.isSameOrBefore(end);
};

export const useUpdateInspection = config =>
  useMutation(async ({ inspection, id }) => {
    const axiosResult = await getApi().put(`/inspections/${id}`, inspection);
    return axiosResult.data;
  }, config);

export const useGetInspections = ({ clientId, params = {} }) => {
  const queryFn = async context => {
    const [{ clientId, params: queryParams }] = context.queryKey;

    const apiParams = {
      page: queryParams.page,
      per_page: queryParams.pageSize,
      ...(queryParams.query && { query: queryParams.query }),
      ...(isValidDateRange(queryParams.startDate, queryParams.endDate)
        ? {
            'by_inspected_at[start_date]': moment(queryParams.startDate).format(SIMPLE_DATE_FORMAT),
            'by_inspected_at[end_date]': moment(queryParams.endDate).format(SIMPLE_DATE_FORMAT)
          }
        : {}),
      ...(queryParams.byStatus && { by_status: queryParams.byStatus }),
      ...(queryParams.type && { by_type: queryParams.type }),
      ...(queryParams.formularyIds && { by_formulary_ids: [queryParams.formularyIds] }),
      ...(queryParams.byProjectIds && { by_project_ids: [queryParams.byProjectIds] }),
      ...(queryParams.published !== null && { by_published: Boolean(queryParams.published) })
    };

    const response = await getApi().get(`/clients/${clientId}/inspections`, {
      params: apiParams
    });

    const transformedData = transformSnake2Camel(response.data);
    return transformedData;
  };

  return useQuery({
    queryKey: [{ clientId, params }, 'GET_all_inspections'],
    queryFn,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
};

export const useGetFormularies = ({ clientId }, config) => {
  const queryFn = async context => {
    const [clientId] = context.queryKey;

    const response = await getApi().get(`/clients/${clientId}/formularies?include=none`);

    return transformSnake2Camel(response.data);
  };

  return useQuery({
    queryKey: [clientId, 'GET_all_formularies'],
    queryFn,
    enabled: !!clientId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    ...config
  });
};
